import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import { LabelTextField } from '../../components/LabelTextField.component';

interface PhoneNumberProps {
  phoneNumber?: string;
  setPhoneNumber: (phoneNumber: string) => void;
}

const PHONE_REGEX = /^\+?(\d{1,3})?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4,}$/;

export const isValidPhoneNumber = (phone: string): boolean => {
  const digitsOnly = phone.replace(/\D/g, '');
  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    return false;
  }
  return PHONE_REGEX.test(phone);
};

export const PhoneNumber: React.FC<PhoneNumberProps> = ({ phoneNumber, setPhoneNumber }) => {
  const [phoneFocused, setPhoneFocused] = React.useState<boolean>(false);
  const [phoneError, setPhoneError] = React.useState<string>('');

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPhoneNumber(value);

    if (value && !isValidPhoneNumber(value)) {
      setPhoneError(getTranslation('Please enter a valid phone number'));
    } else {
      setPhoneError('');
    }
  };

  return (
    <Layout.Column style={{ height: '100%' }}>
      <OpenSans.Primary size="xl-28" weight="bold-700">
        {getTranslation('Secure your account')}
      </OpenSans.Primary>

      <Spacer.Vertical size={4} />

      <OpenSans.Secondary>
        {getTranslation(
          'Adding a phone number will help us verify your account and keep it secure.',
        )}
      </OpenSans.Secondary>

      <Spacer.Vertical size={32} />

      <LabelTextField
        label={getTranslation('Phone Number')}
        active={phoneFocused}
        error={!!phoneError}
        errorMessage={phoneError}
      >
        <Layout.Row align py={8}>
          <Icon
            name="call-outline"
            size={24}
            color={phoneFocused ? theme.colors.brandPrimary : theme.colors.primary}
          />

          <Spacer.Horizontal />

          <OpenSans.Input
            name="phoneNumber"
            value={phoneNumber}
            type="tel"
            weight="light-300"
            placeholder="+1 (555) 555-5555"
            grow
            style={{ outline: 'none' }}
            onChange={handlePhoneChange}
            autoComplete="tel"
            onFocus={() => setPhoneFocused(true)}
            onBlur={() => setPhoneFocused(false)}
          />
        </Layout.Row>
      </LabelTextField>
    </Layout.Column>
  );
};
